import {
  INTEGRATION_TYPES,
  type IntegrationDefinition,
  type IntegrationEndpointInfo,
} from '../types'

export const DEFAULT_ENERGOV_EPL_CONFIG = {}

const ENERGOV_EPL_ENDPOINTS: IntegrationEndpointInfo[] = [
  {
    name: 'Get Permit Details',
    method: 'GET',
    description: 'Look up a permit by number and return full details.',
    toolName: 'get_permit_details',
  },
]

export const energovEplIntegration: IntegrationDefinition = {
  id: INTEGRATION_TYPES.ENERGOV_EPL,
  title: 'Tyler EnerGov (EPL)',
  defaultConnectionName: 'EnerGov EPL',
  logo: 'https://polimorphic-static.s3.us-east-2.amazonaws.com/3p-logos/tyler-energov-logo.png',
  icon: 'https://polimorphic-static.s3.us-east-2.amazonaws.com/3p-logos/tyler-icon.png',
  cardDescription:
    'Connect to Tyler EnerGov to look up permits, check permit status, and retrieve permit documents.',
  modalDescription:
    'Connect to the Tyler EnerGov Permit & Licensing (EPL) REST API to enable permit searches and full permit detail lookups. Authentication is handled automatically via cookie session.',
  formFields: [
    {
      name: 'host',
      label: 'API Host',
      placeholder: 'e.g., your-org-energovapi.tylerhost.net',
      helperText: 'The EnerGov API host for your organization (without https://)',
      required: true,
      type: 'text',
    },
    {
      name: 'citizenPortalBaseUrl',
      label: 'Citizen Portal Base URL (Optional)',
      placeholder:
        'e.g., https://your-org-energovpub.tylerhost.net/apps/civic-access-public/details/permit/',
      helperText:
        'Full base URL for public permit links. If left blank, this is auto-derived from the API host using the default path /apps/civic-access-public/details/permit/. Only fill this in if your citizen portal host or path differs from the default.',
      required: false,
      type: 'text',
    },
    {
      name: 'username',
      label: 'Username',
      placeholder: 'Enter your EnerGov API username',
      helperText: 'Your EnerGov username is stored securely and encrypted',
      required: true,
      type: 'text',
    },
    {
      name: 'password',
      label: 'Password',
      placeholder: 'Enter your EnerGov API password',
      helperText: 'Your EnerGov password is stored securely and encrypted',
      required: true,
      type: 'password',
    },
  ],
  apiConfig: {
    createEndpoint: '/integrations/generate-energov-epl-integration/',
    buildPayload: (formData, config, organizationId, organizationIdentityId) => {
      const host = formData.host.replace(/^https?:\/\//, '').replace(/\/+$/, '')
      const rawPortalUrl = (formData.citizenPortalBaseUrl || '').trim()

      let civicAccessHost = ''
      let civicAccessPath = ''

      if (rawPortalUrl) {
        const withScheme = rawPortalUrl.startsWith('http')
          ? rawPortalUrl
          : `https://${rawPortalUrl}`
        try {
          const parsed = new URL(withScheme)
          civicAccessHost = parsed.host
          civicAccessPath = parsed.pathname.replace(/\/+$/, '')
        } catch {
          civicAccessHost = rawPortalUrl.replace(/^https?:\/\//, '').split('/')[0]
          const pathStart = rawPortalUrl.replace(/^https?:\/\//, '').indexOf('/')
          civicAccessPath =
            pathStart >= 0
              ? rawPortalUrl
                  .replace(/^https?:\/\//, '')
                  .slice(pathStart)
                  .replace(/\/+$/, '')
              : ''
        }
      } else {
        civicAccessHost = host.replace('energovapi', 'energovpub')
      }

      return {
        organization_identity_id: organizationIdentityId,
        username: formData.username,
        password: formData.password,
        host,
        civic_access_host: civicAccessHost,
        civic_access_path: civicAccessPath,
        connection_name: formData.connectionName || 'EnerGov EPL',
        excluded_endpoints: (config.excluded_endpoints as string[]) || [],
      }
    },
    authTokenName: 'username',
    buildTestCredentials: (formData) => {
      const host = formData.host.replace(/^https?:\/\//, '').replace(/\/+$/, '')
      return {
        host,
        username: formData.username,
        password: formData.password,
      }
    },
  },
  defaultConfig: { ...DEFAULT_ENERGOV_EPL_CONFIG },
  endpoints: ENERGOV_EPL_ENDPOINTS,
  hostValidation: {
    pattern: /^[a-zA-Z0-9.-]+\.tylerhost\.net/,
    example: 'your-org-energovapi.tylerhost.net',
  },
}
